import React, { createContext, Dispatch, SetStateAction, useContext, useState } from 'react'

import { ImageArrayValue } from '@makyoapp/makyo-common'

export enum ImageManagerKind {
  Upload = 'upload',
  ImageUrl = 'imageUrl',
  MyImages = 'myImages',
  StockPhotos = 'stockPhotos',
  OpenAI = 'openai',
}

export type ImageManagerChangeHandler = (ty: ImageManagerKind, value: unknown) => void

export interface ImageManagerNavLabelProps {
  upload?: string
  imageUrl?: string
  myImages?: string
  stockPhotos?: string
  openAI?: string
}

interface ImageManagerValue {
  url: string
}

/**
 * State
 */
interface ImageManagerState {
  id?: string
  value: ImageManagerValue
  setValue: (value: ImageManagerValue) => void
  show: boolean
  onHide?: () => void
  title?: string
  onChange: ImageManagerChangeHandler
  navLabels?: ImageManagerNavLabelProps
  smallPreview: boolean
  maxHeight: string
  multiple: boolean
  selectedImages: ImageArrayValue[]
  setSelectedImages: Dispatch<SetStateAction<ImageArrayValue[]>>
  onSelect?: (kind: ImageManagerKind | null) => void
  forceUrlPanelHeight?: boolean
}

const ImageManagerContext = createContext<ImageManagerState>({
  value: {
    url: '',
  },
  setValue: () => null,
  show: false,
  onHide: () => null,
  title: '',
  onChange: () => null,
  navLabels: undefined,
  smallPreview: false,
  maxHeight: '60vh',
  multiple: false,
  selectedImages: [],
  setSelectedImages: () => null,
  onSelect: () => null,
})

export function useImageManagerContext() {
  return useContext(ImageManagerContext)
}

export interface ImageManagerProviderProps {
  id?: string
  children?: React.ReactNode
  initialValue?: ImageManagerValue
  initialSelected?: ImageArrayValue[]
  /**
   * show / hide state when using modal
   */
  show?: boolean
  /**
   * on hide when using modal
   */
  onHide?: () => void
  title?: string
  onChange: ImageManagerChangeHandler
  navLabels?: ImageManagerNavLabelProps
  smallPreview?: boolean
  maxHeight?: string
  multiple?: boolean
  onSelect?: (kind: ImageManagerKind | null) => void
  forceUrlPanelHeight?: boolean
}

/**
 * DbContext provider
 */
export const ImageManagerProvider = ({
  id,
  children,
  initialValue,
  initialSelected,
  show = false,
  onHide,
  title,
  onChange,
  navLabels,
  smallPreview = false,
  maxHeight,
  multiple = false,
  onSelect,
  forceUrlPanelHeight,
}: ImageManagerProviderProps) => {
  const [value, setValue] = useState<ImageManagerValue>(
    initialValue || {
      url: '',
    }
  )

  const [selectedImages, setSelectedImages] = useState<ImageArrayValue[]>(initialSelected ?? [])

  return (
    <ImageManagerContext.Provider
      value={{
        id,
        value,
        setValue,
        show,
        onHide,
        title,
        onChange,
        navLabels,
        smallPreview,
        maxHeight: maxHeight ? maxHeight : multiple ? '50vh' : '60vh',
        multiple,
        selectedImages,
        setSelectedImages,
        onSelect,
        forceUrlPanelHeight,
      }}
    >
      {children}
    </ImageManagerContext.Provider>
  )
}
